import { useCallback, useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import { successToast } from './successToast';
import { previewBookingLimit, saveBookingLimit } from 'api/marketing';
import {
    IBookingLimitPreviewItem,
    IBookingLimitRule,
    IBookingLimitSaveReq,
} from 'types/bookingLimit';
import { IDraft } from './LimitRow';
import { validateDraft, WEEKLY } from './helpers';

const toReq = (draft: IDraft): IBookingLimitSaveReq =>
    draft.enabled
        ? { periodType: WEEKLY, enabled: true, limitCount: draft.limitCount as number }
        : { periodType: WEEKLY, enabled: false };

/**
 * 上限编辑的草稿态与保存流程。Practice View 与 My Marketing 共用。
 *
 * 保存一律走「先 preview 再 save」：调低会立刻把超标成员从搜索里摘掉，
 * 确认框需要 preview 返回的 willPause 才能点名 —— 前端自己算会和后端口径漂移。
 */
export const useLimitEditor = (
    saved: IBookingLimitRule | undefined,
    onSaved: () => void,
    isPractice = false,
) => {
    const initial: IDraft = useMemo(
        () => ({ enabled: !!saved?.enabled, limitCount: saved?.limitCount ?? null }),
        [saved?.enabled, saved?.limitCount],
    );

    const [draft, setDraft] = useState<IDraft>(initial);
    const [saving, setSaving] = useState(false);
    const [willPause, setWillPause] = useState<IBookingLimitPreviewItem[]>([]);
    const [confirmSave, setConfirmSave] = useState(false);
    const [confirmOff, setConfirmOff] = useState(false);

    // 外部数据刷新（例如另一个 tab 保存后 refetch）时丢弃旧草稿，避免显示已经不存在的值
    useEffect(() => {
        setDraft(initial);
    }, [initial]);

    const errMsg = validateDraft(draft.enabled, draft.limitCount);

    const dirty =
        draft.enabled !== initial.enabled ||
        (draft.enabled && draft.limitCount !== initial.limitCount);

    const reset = useCallback(() => setDraft(initial), [initial]);

    /** 真正落库。确认框点「确定」和无需确认时都走这里。 */
    const commit = useCallback(async () => {
        try {
            setSaving(true);
            const result = await saveBookingLimit(toReq(draft));
            if (result.error) {
                // 后端文案是写给终端用户的，直接展示
                message.error(result.error);
                return;
            }
            /* 设计稿是三分支，不是两分支（原型 patient-limits.html:961）：
               首次打开说「turned on at N」，已开着改数字说「changed to N」——
               provider 需要知道刚才那一下是「新建」还是「修改」。
               practice 视图再加「for the practice」，否则 owner 分不清改的是谁的。 */
            const who = isPractice ? ' for the practice' : '';
            let what: string;
            if (!draft.enabled) {
                what = 'turned off.';
            } else if (!initial.enabled) {
                what = `turned on at ${draft.limitCount}.`;
            } else {
                what = `changed to ${draft.limitCount}.`;
            }
            successToast(`Weekly limit${who} ${what}`);
            setConfirmSave(false);
            setConfirmOff(false);
            // 响应体是 null，状态一律重新拉取
            onSaved();
        } catch (e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    }, [draft, onSaved, initial.enabled, isPractice]);

    /** 点 Save：先问后端这次改动会影响谁，再决定是否弹确认框。 */
    const submit = useCallback(async () => {
        if (errMsg || !dirty) {
            return;
        }
        // 关闭开关单独确认：语义是「放回不限量」，和调低不是一回事
        if (!draft.enabled) {
            setConfirmOff(true);
            return;
        }
        try {
            setSaving(true);
            const result = await previewBookingLimit(toReq(draft));
            const hit: IBookingLimitPreviewItem[] = result?.data?.data?.willPause ?? [];
            if (hit.length) {
                setWillPause(hit);
                setConfirmSave(true);
                return;
            }
        } catch (e) {
            console.error(e);
            return;
        } finally {
            setSaving(false);
        }
        await commit();
    }, [draft, dirty, errMsg, commit]);

    return {
        draft,
        setDraft,
        dirty,
        errMsg,
        saving,
        willPause,
        confirmSave,
        confirmOff,
        setConfirmSave,
        setConfirmOff,
        submit,
        commit,
        reset,
    };
};
